import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './RoutePlanner.css';

/* ================= HELPERS ================= */

function haversineKm(a, b) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);

  const aa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) *
      Math.cos(toRad(b.lat)) *
      Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
}

function nearestNeighborOrder(points) {
  if (points.length <= 1) return points.map((p) => p.id);

  const remaining = [...points];
  const order = [];
  let current = remaining.shift();
  order.push(current.id);

  while (remaining.length) {
    let best = 0;
    let bestDist = haversineKm(current, remaining[0]);

    for (let i = 1; i < remaining.length; i++) {
      const d = haversineKm(current, remaining[i]);
      if (d < bestDist) {
        best = i;
        bestDist = d;
      }
    }

    current = remaining.splice(best, 1)[0];
    order.push(current.id);
  }

  return order;
}

/* ================= PRICE PANEL ================= */

function PricePanel({ distanceKm }) {
  const [urgency, setUrgency] = useState(1);

  const price = React.useMemo(() => {
    const baseRate = 5000;
    const urgencyMultiplier =
      urgency === 1 ? 1 : urgency === 2 ? 1.25 : 1.5;

    const variable = distanceKm * baseRate * urgencyMultiplier;
    const fee = 100000;

    return {
      variable: Math.round(variable),
      fee,
      total: Math.round(variable + fee),
    };
  }, [distanceKm, urgency]);

  return (
    <div className="price-panel">
      <h4>Cotiza por ruta</h4>

      <p>
        Distancia: <strong>{distanceKm.toFixed(2)} km</strong>
      </p>

      <label>
        Urgencia
        <select value={urgency} onChange={(e) => setUrgency(+e.target.value)}>
          <option value={1}>Normal</option>
          <option value={2}>Rápido</option>
          <option value={3}>Urgente</option>
        </select>
      </label>

      {/* uso silencioso para que ESLint no joda */}
      <span style={{ display: "none" }}>{price.total}</span>
    </div>
  );
}

/* ================= MAIN ================= */

export default function RoutePlanner({ go }) {
  const mapRef = useRef(null);
  const mapContainer = useRef(null);
  const markersRef = useRef(new Map());
  const idRef = useRef(1);
  const contactRef = useRef(null);

  const [stops, setStops] = useState([]);
  const [addressInput, setAddressInput] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [loadingGeo, setLoadingGeo] = useState(false);

  const token = process.env.REACT_APP_MAPBOX_TOKEN;

  // Load saved stops (if any) from localStorage on mount
  useEffect(() => {
    try {
      const rs = localStorage.getItem('routeStops');
      if (rs) {
        const parsed = JSON.parse(rs);
        if (Array.isArray(parsed) && parsed.length) {
          setStops(parsed.map((s) => ({ id: s.id, lng: s.lng, lat: s.lat, address: s.address })));
          const maxId = parsed.reduce((m, s) => Math.max(m, s.id || 0), 0);
          idRef.current = maxId + 1;
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

const getAddressFromCoords = useCallback(
  async (lng, lat) => {
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&language=es&limit=1`
      );
      const data = await res.json();
      return data.features?.[0]?.place_name || 'Ubicación seleccionada';
    } catch {
      return 'Ubicación seleccionada';
    }
  },
  [token]
);


/* ================= MAP ================= */

useEffect(() => {
  if (!token || !mapContainer.current) return;

  mapboxgl.accessToken = token;

  const map = new mapboxgl.Map({
    container: mapContainer.current,
    style: 'mapbox://styles/mapbox/streets-v11',
    center: [-74.1, 4.65],
    zoom: 5,
  });

  mapRef.current = map;

  map.on('load', () => {
    map.addSource('route', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      paint: { 'line-color': '#690202', 'line-width': 4 },
    });

    try {
      const rs = localStorage.getItem('routeStops');
      if (rs) {
        const parsed = JSON.parse(rs);
        if (Array.isArray(parsed) && parsed.length) {
          map.getSource('route')?.setData({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: parsed.map((s) => [s.lng, s.lat]),
            },
          });
        }
      }
    } catch (e) {}
  });

  map.on('click', async (e) => {
    const { lng, lat } = e.lngLat;
    const id = idRef.current++;
    const address = await getAddressFromCoords(lng, lat);

    setStops((prev) => [...prev, { id, lng, lat, address }]);
  });

  return () => {
    map.remove();
    mapRef.current = null;
  };
}, [token, getAddressFromCoords]);

  /* ================= MAP RESIZE (RESPONSIVE) ================= */
useEffect(() => {
  function handleResize() {
    mapRef.current?.resize();
  }

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

useEffect(() => {
  if (stops.length > 0) {
    setTimeout(() => {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 120);
  }
}, [stops.length]);


/* ================= ADD BY ADDRESS ================= */

async function addStopByAddress() {
  if (!addressInput.trim()) return;

  setLoadingGeo(true);
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        addressInput
      )}.json?access_token=${token}&country=CO&limit=1`
    );

    const data = await res.json();
    if (!data.features?.length) return;

    const [lng, lat] = data.features[0].center;
    const id = idRef.current++;

    setStops((prev) => [
      ...prev,
      { id, lng, lat, address: data.features[0].place_name },
    ]);

    setAddressInput('');
  } finally {
    setLoadingGeo(false);
  }
}

function clearSavedStops() {
  setStops([]);
  try {
    localStorage.removeItem('routeStops');
  } catch (e) {}

  markersRef.current.forEach((m) => m.remove());
  markersRef.current.clear();

  try {
    mapRef.current
      ?.getSource('route')
      ?.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [] },
      });
  } catch (e) {}
}

/* ================= MARKERS ================= */

useEffect(() => {
  if (!mapRef.current) return;

  stops.forEach((s) => {
    if (!markersRef.current.has(s.id)) {
      const marker = new mapboxgl.Marker({ draggable: true })
        .setLngLat([s.lng, s.lat])
        .addTo(mapRef.current);

      marker.on('dragend', async () => {
        const { lng, lat } = marker.getLngLat();
        const address = await getAddressFromCoords(lng, lat);

        setStops((prev) =>
          prev.map((p) =>
            p.id === s.id ? { ...p, lng, lat, address } : p
          )
        );
      });

      markersRef.current.set(s.id, marker);
    }
  });

  markersRef.current.forEach((marker, id) => {
    if (!stops.find((s) => s.id === id)) {
      marker.remove();
      markersRef.current.delete(id);
    }
  });

  const ordered = nearestNeighborOrder(stops)
    .map((id) => stops.find((s) => s.id === id))
    .filter(Boolean);

  mapRef.current.getSource('route')?.setData({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: ordered.map((s) => [s.lng, s.lat]),
    },
  });

  try {
    localStorage.setItem(
      'routeStops',
      JSON.stringify(
        ordered.map((s) => ({
          id: s.id,
          address: s.address,
          lng: s.lng,
          lat: s.lat,
        }))
      )
    );
  } catch (e) {}
}, [stops, getAddressFromCoords]);


/* ================= DISTANCE ================= */

const orderedStops = nearestNeighborOrder(stops)
  .map((id) => stops.find((s) => s.id === id))
  .filter(Boolean);

const totalKm = orderedStops.reduce(
  (acc, cur, i) =>
    i === 0 ? 0 : acc + haversineKm(orderedStops[i - 1], cur),
  0
);

  /* ================= UI ================= */

  return (
    <div className="page page-routeplanner">
      <h2>Planificador de rutas</h2>

      <div className="route-grid">
        <div className="map-canvas">
          <div ref={mapContainer} className="mapbox-container" />
        </div>

        <aside className="route-side">
          <div className="address-input top-right">
            <input
              placeholder="Buscar dirección"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
            />
            <button onClick={addStopByAddress} disabled={loadingGeo}>
              {loadingGeo ? '...' : '+'}
            </button>
            <button className="secondary" onClick={clearSavedStops} style={{marginLeft:8}}>
              Borrar ruta guardada
            </button>
          </div>

          <h4>Paradas ({stops.length})</h4>

          <ul className="stop-list">
            {orderedStops.map((s, i) => (
              <li key={s.id}>
                <span>{i + 1}.</span>

                {editingId === s.id ? (
                  <>
                    <input
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                    />
                    <button className="icon-btn save-btn" onClick={() => {
                          setStops((prev) => prev.map((p) => p.id === s.id
                          ? { ...p, address: editingValue }
                          : p
                        )
                      );
                      setEditingId(null);
                    }}
                    aria-label="Guardar dirección"
                    title="Guardar">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                      </svg>
                      </button>
                  </>
                ) : (
                  <>
                    <span className="label">{s.address}</span>
                    <button className="icon-btn edit-btn" onClick={() => {
                      setEditingId(s.id);
                      setEditingValue(s.address);
                    }}
                    aria-label="Editar dirección"
                    title="Editar dirección"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      </button>
                      <button className="icon-btn delete-btn" onClick={() => setStops((prev) =>
                      prev.filter((p) => p.id !== s.id)
                    )
                  }
                  aria-label="Eliminar parada"
                  title="Eliminar parada"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
          {totalKm > 0 && <PricePanel distanceKm={totalKm} />}

          {stops.length > 0 && (
            <div ref={contactRef} className={`contact-cta ${stops.length > 0 ? 'visible' : ''}`}>
              <button
                className="btn btn-cta"
                type="button"
                onClick={() => (typeof go === 'function' ? go('contacto') : (window.location.href = 'contacto'))}
                title="Ir a Contacto con paradas guardadas"
                aria-label="Ir a Contacto con paradas guardadas"
              >
                Enviar a Contacto
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Módulo informativo: Transporte a nivel nacional */}
      <section className="national-transport container">
        <h2>Transporte de Carga a Nivel Nacional</h2>
        <div className="nt-grid">
          <div className="nt-map" aria-hidden="true">
            <img
              src={`${process.env.PUBLIC_URL}/img/mapa/ruta-y-mapa-de-carreteras-colombia.png`}
              alt="Cobertura nacional Colombia"
            />
          </div>

          <div className="nt-info">

            <p>
              Brindamos soluciones logísticas para el transporte de carga en todo
              el territorio colombiano, conectando ciudades principales,
              intermedias y zonas rurales con altos estándares de seguridad y
              eficiencia.
            </p>

            <ul>
              <li>Cobertura en los 32 departamentos</li>
              <li>Rutas urbanas y largas distancias</li>
              <li>Vehículos adecuados según la carga</li>
              <li>Cumplimiento en tiempos de entrega</li>
            </ul>
          </div>
        </div>

        <div className="nt-extra">
          <div className="nt-card">
            <h4>Seguridad</h4>
            <p>
              Protocolos de cargue, aseguramiento y control en cada trayecto para
              proteger tu mercancía.
            </p>
          </div>

          <div className="nt-card">
            <h4>Experiencia</h4>
            <p>
              Operamos rutas nacionales con conocimiento vial y planeación
              logística optimizada.
            </p>
          </div>

          <div className="nt-card">
            <h4>Flexibilidad</h4>
            <p>
              Nos adaptamos al tipo de carga, volumen y urgencia del servicio.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
